import { useState } from 'react';
import { bookingCostCents } from '../../../shared/crewCost';
import { CREW_COST_ERROR_TITLE, crewCalcLabel, crewDateLabel, dateRange, halfSuggestionCents, parseHalfDayDates } from '../../lib/projectDetailShared';
import { type CrewContactRow, type ProjectCrewConflict, type ProjectCrewWithName } from '../../../shared/types';
import { DateField, Dropdown, formatMoney, formatWeekdayDay, parseMoney, Pill, TrashAction } from '../../components/primitives';
import { reconcileOnRangeChange } from './helpers';

/** Per-day Full/Half toggle row (mockups-feature-builds-2026-07.html §4b) —
    one chip per calendar day in the booking's date range; tap flips it.
    Shared between AddCrewBooking and CrewEntryRow's edit mode. */
function DayChipRow({
  start,
  end,
  halfDays,
  onToggle,
}: {
  start: string;
  end: string;
  halfDays: Set<string>;
  onToggle: (date: string) => void;
}) {
  const days = dateRange(start, end);
  if (days.length === 0) return null;
  return (
    <div className="f-field">
      <span className="f-label">Days</span>
      <div className="rate-chip-row">
        {days.map((d) => {
          const half = halfDays.has(d);
          return (
            <span key={d} className={`rate-chip ${half ? 'half' : 'full'}`} onClick={() => onToggle(d)}>
              {formatWeekdayDay(d)}
              {half && <span className="half-glyph">½</span>}
            </span>
          );
        })}
      </div>
      <span className="f-help">Tap a day to toggle full / half</span>
    </div>
  );
}

/** Inline "Half-day rate" money field — revealed once any day is toggled to
    Half. Prefilled from the register when it has a value (same snapshot
    contract as the day rate above it); when it doesn't, the field opens
    empty with the 50%-of-day-rate suggestion as a dim placeholder only —
    never auto-filled — and the caller blocks Add/Save until a real number
    is typed (Henry's binding half-day semantics). */
function HalfDayRateField({
  value,
  onChange,
  suggestionCents,
}: {
  value: string;
  onChange: (v: string) => void;
  suggestionCents: number | null;
}) {
  const missing = value.trim() === '';
  return (
    <div className="f-field">
      <span className="f-label">
        Half-day rate {missing && <span className="req">*</span>}
      </span>
      <input
        className="f-input money"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={suggestionCents !== null ? `suggested: ${formatMoney(suggestionCents)}` : '$0'}
      />
    </div>
  );
}

/** One "On this job" booking (Phase 3.3, mockup #4) — display mode shows the
    name/rate/role/dates plus the computed cost line and any conflict pill
    from the last save; edit mode swaps in the same compact field set as the
    add flow, including the per-day chip row (crew member itself isn't
    editable — reassign by removing and re-adding). */
function CrewEntryRow({
  booking,
  conflicts,
  onSave,
  onRemove,
}: {
  booking: ProjectCrewWithName;
  conflicts: ProjectCrewConflict[];
  onSave: (fields: Record<string, unknown>) => Promise<void>;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(booking.role ?? '');
  const [rate, setRate] = useState((booking.day_rate_cents / 100).toString());
  const [start, setStart] = useState<string | null>(booking.start_date);
  const [end, setEnd] = useState<string | null>(booking.end_date);
  const [halfDays, setHalfDays] = useState<Set<string>>(() => new Set(parseHalfDayDates(booking.half_day_dates)));
  const [halfRate, setHalfRate] = useState(
    booking.half_day_rate_cents === null ? '' : (booking.half_day_rate_cents / 100).toString(),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleHalfDay = (date: string) => {
    setHalfDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };
  const halfRateMissing = halfDays.size > 0 && halfRate.trim() === '';

  const openEdit = () => {
    setRole(booking.role ?? '');
    setRate((booking.day_rate_cents / 100).toString());
    setStart(booking.start_date);
    setEnd(booking.end_date);
    setHalfDays(new Set(parseHalfDayDates(booking.half_day_dates)));
    setHalfRate(booking.half_day_rate_cents === null ? '' : (booking.half_day_rate_cents / 100).toString());
    setError(null);
    setEditing(true);
  };

  const submit = async () => {
    if (!start || !end || busy || halfRateMissing) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        role: role.trim() || null,
        day_rate_cents: parseMoney(rate),
        start_date: start,
        end_date: end,
        half_day_dates: [...halfDays],
        half_day_rate_cents: halfRate.trim() === '' ? null : parseMoney(halfRate),
      });
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update booking.');
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="crew-entry">
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="f-field" style={{ flex: 1 }}>
              <span className="f-label">Start</span>
              <DateField
                value={start}
                onChange={(v) => {
                  setStart(v);
                  reconcileOnRangeChange(setHalfDays, v, end);
                }}
              />
            </div>
            <div className="f-field" style={{ flex: 1 }}>
              <span className="f-label">End</span>
              <DateField
                value={end}
                onChange={(v) => {
                  setEnd(v);
                  reconcileOnRangeChange(setHalfDays, start, v);
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="f-field" style={{ flex: 1 }}>
              <span className="f-label">Role</span>
              <input className="f-input" value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
            <div className="f-field" style={{ flex: 1 }}>
              <span className="f-label">Day rate</span>
              <input className="f-input money" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
          </div>
          {start && end && <DayChipRow start={start} end={end} halfDays={halfDays} onToggle={toggleHalfDay} />}
          {halfDays.size > 0 && (
            <HalfDayRateField value={halfRate} onChange={setHalfRate} suggestionCents={halfSuggestionCents(rate)} />
          )}
          {error && <p className="error-text">{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn primary" type="button" onClick={submit} disabled={busy || halfRateMissing}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button className="btn" type="button" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  const cost = bookingCostCents(booking);

  return (
    <div className="crew-entry">
      <div className="crew-top">
        <span className="crew-name">{booking.crew_name}</span>
        <span className="crew-rate">
          {formatMoney(booking.day_rate_cents)}/day
          {booking.half_day_rate_cents !== null && (
            <span style={{ color: 'var(--text-faint)' }}> · {formatMoney(booking.half_day_rate_cents)}/half</span>
          )}
        </span>
      </div>
      <div className="crew-sub">
        <span className="crew-role">{booking.role || '—'}</span>
        <span className="crew-dates">{crewDateLabel(booking.start_date, booking.end_date)}</span>
      </div>
      {cost === null ? (
        <div className="crew-calc" title={CREW_COST_ERROR_TITLE}>
          —
        </div>
      ) : (
        <div className="crew-calc">{crewCalcLabel(cost)}</div>
      )}
      {conflicts.length > 0 && (
        <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
          {conflicts.map((cf, i) => (
            <div key={i}>
              <Pill tone="danger">
                ⚠ double-booked — also on {cf.project_name}, {crewDateLabel(cf.start_date, cf.end_date)}
              </Pill>
            </div>
          ))}
        </div>
      )}
      <div className="crew-actions">
        <a className="group-link" style={{ cursor: 'pointer' }} onClick={openEdit}>
          Edit
        </a>
        <TrashAction label="Remove" onConfirm={onRemove} />
      </div>
    </div>
  );
}

/** "+ Add crew" compact inline flow (Phase 3.3, mockup #4) — crew picker
    from the register, date range, role, day rate prefilled from the
    register's day_rate_cents, and the per-day Full/Half chip row (§4b)
    with its own half-day rate prefill/prompt. */
function AddCrewBooking({
  register,
  today,
  onAdd,
}: {
  register: CrewContactRow[];
  today: string;
  onAdd: (fields: {
    crew_id: string;
    role: string | null;
    day_rate_cents: number | null;
    start_date: string;
    end_date: string;
    half_day_dates: string[];
    half_day_rate_cents: number | null;
  }) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [crewOpen, setCrewOpen] = useState(false);
  const [crewId, setCrewId] = useState<string | null>(null);
  const [role, setRole] = useState('');
  const [rate, setRate] = useState('');
  const [start, setStart] = useState<string | null>(today);
  const [end, setEnd] = useState<string | null>(today);
  const [halfDays, setHalfDays] = useState<Set<string>>(new Set());
  const [halfRate, setHalfRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = register.find((r) => r.id === crewId) ?? null;
  const toggleHalfDay = (date: string) => {
    setHalfDays((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };
  const halfRateMissing = halfDays.size > 0 && halfRate.trim() === '';

  const reset = () => {
    setCrewId(null);
    setRole('');
    setRate('');
    setStart(today);
    setEnd(today);
    setHalfDays(new Set());
    setHalfRate('');
    setError(null);
    setOpen(false);
  };

  const submit = async () => {
    if (!crewId || !start || !end || busy || halfRateMissing) return;
    setBusy(true);
    setError(null);
    try {
      await onAdd({
        crew_id: crewId,
        role: role.trim() || null,
        day_rate_cents: rate.trim() === '' ? null : parseMoney(rate),
        start_date: start,
        end_date: end,
        half_day_dates: [...halfDays],
        half_day_rate_cents: halfRate.trim() === '' ? null : parseMoney(halfRate),
      });
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add booking.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <a
        className="group-link"
        style={{ cursor: 'pointer', display: 'inline-block', marginTop: 10 }}
        onClick={() => setOpen(true)}
      >
        + Add crew
      </a>
    );
  }

  return (
    <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
      <div className="f-field">
        <span className="f-label">Crew member</span>
        <div className="dropdown-anchor">
          <div className="f-select" onClick={() => setCrewOpen((o) => !o)}>
            {selected ? <span>{selected.name}</span> : <span className="ph">Select…</span>}
            <span className="caret">▾</span>
          </div>
          {crewOpen && (
            <Dropdown
              items={register.map((r) => ({ value: r.id, label: r.name }))}
              value={crewId}
              searchable
              onSelect={(v) => {
                setCrewId(v);
                const contact = register.find((r) => r.id === v);
                setRate(contact?.day_rate_cents != null ? (contact.day_rate_cents / 100).toString() : '');
                setHalfRate(contact?.half_day_rate_cents != null ? (contact.half_day_rate_cents / 100).toString() : '');
              }}
              onClose={() => setCrewOpen(false)}
            />
          )}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Start</span>
          <DateField
            value={start}
            onChange={(v) => {
              setStart(v);
              reconcileOnRangeChange(setHalfDays, v, end);
            }}
          />
        </div>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">End</span>
          <DateField
            value={end}
            onChange={(v) => {
              setEnd(v);
              reconcileOnRangeChange(setHalfDays, start, v);
            }}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Role</span>
          <input className="f-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="DP, sound…" />
        </div>
        <div className="f-field" style={{ flex: 1 }}>
          <span className="f-label">Day rate</span>
          <input className="f-input money" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="$0" />
        </div>
      </div>
      {start && end && <DayChipRow start={start} end={end} halfDays={halfDays} onToggle={toggleHalfDay} />}
      {halfDays.size > 0 && (
        <HalfDayRateField value={halfRate} onChange={setHalfRate} suggestionCents={halfSuggestionCents(rate)} />
      )}
      {error && <p className="error-text">{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary" type="button" onClick={submit} disabled={!crewId || busy || halfRateMissing}>
          {busy ? 'Adding…' : 'Add'}
        </button>
        <button className="btn" type="button" onClick={reset} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export { CrewEntryRow, AddCrewBooking };
